// API Response type
export interface ApiResponse<T> {
    success: boolean;
    data: T;
}

// Re-export types from the service for consistency
export type { QrUploadSession, QrUploadFile } from "@functions/services/qrUpload/qrUploadService";

/**
 * Create a new QR upload session
 */
export async function createUploadSession(
    workspaceUrl?: string
): Promise<ApiResponse<QrUploadSession>> {
    const response = await fetch("/query/qr/upload-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspaceUrl })
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: "Failed to create session" }));
        throw new Error(error.error || "Failed to create session");
    }

    const data = await response.json();
    return { success: true, data };
}

/**
 * Get QR upload session information
 */
export async function getUploadSession(sessionId: string): Promise<ApiResponse<QrUploadSession>> {
    const url = `/query/qr/upload-session/${sessionId}`;

    const response = await fetch(url, { method: "GET" });

    if (!response.ok) {
        const errorText = await response.text();

        let error;
        try {
            error = JSON.parse(errorText);
        } catch {
            error = { error: errorText || "Failed to get session" };
        }

        throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return { success: true, data };
}

/**
 * Get a signed download URL for a file in a QR upload session
 */
export async function getQrFileDownloadUrl(
    sessionId: string,
    fileId: string
): Promise<ApiResponse<{ downloadUrl: string }>> {
    const response = await fetch(
        `/query/qr/upload-session/${sessionId}/file/${fileId}/download-url`,
        { method: "GET" }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: "Failed to get download URL" }));
        throw new Error(error.error || "Failed to get download URL");
    }

    const data = await response.json();
    return { success: true, data };
}

/**
 * Clear processed files from a QR upload session
 */
export async function clearProcessedQrFiles(
    sessionId: string,
    fileIds: string[]
): Promise<ApiResponse<{ success: boolean }>> {
    const response = await fetch(`/query/qr/upload-session/${sessionId}/clear-files`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileIds })
    });

    if (!response.ok) {
        const error = await response
            .json()
            .catch(() => ({ error: "Failed to clear processed files" }));
        throw new Error(error.error || "Failed to clear processed files");
    }

    const data = await response.json();
    return { success: true, data };
}

/**
 * Delete a QR upload session
 */
export async function deleteUploadSession(
    sessionId: string
): Promise<ApiResponse<{ success: boolean }>> {
    const response = await fetch(`/query/qr/upload-session/${sessionId}`, { method: "DELETE" });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: "Failed to delete session" }));
        throw new Error(error.error || "Failed to delete session");
    }

    const data = await response.json();
    return { success: true, data };
}
